import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import apiService from "../services/api";

const FeaturedOffers = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");

  useEffect(() => {
    const fetchFeaturedCoupons = async () => {
      try {
        setLoading(true);
        const response = await apiService.getFeaturedCoupons();
        if (response.status === 'success' && response.data.coupons) {
          setCoupons(response.data.coupons);
        }
      } catch (error) {
        console.error('Failed to fetch featured coupons:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedCoupons();
  }, []);

  if (loading) {
    return null;
  }

  if (!coupons || coupons.length === 0) {
    return null;
  }

  const getDiscountText = (coupon) => {
    if (coupon.type === 'percentage') {
      return `${coupon.value}% OFF`;
    } else if (coupon.type === 'fixed') {
      return `₹${coupon.value} OFF`;
    }
    return 'Special Offer';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setShowDialog(true);
    } catch (error) {
      console.error('Failed to copy code:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedCode(code);
        setShowDialog(true);
      } catch (err) {
        console.error('Fallback copy failed:', err);
        alert('Failed to copy code. Please copy manually: ' + code);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <section
      className="w-full py-8 sm:py-12 md:py-16 bg-white"
      style={{ fontFamily: '"Bricolage Grotesque", sans-serif' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-10"
        >
          <div className="text-center sm:text-left">
            <h2 className="text-xl sm:text-5xl font-bold mb-2 sm:mb-3 text-black">
              Featured Offers
            </h2>
            <p className="text-xs sm:text-xl text-gray-600 max-w-3xl mx-auto sm:mx-0">
              Don't miss out on these exclusive discounts and special promotions!
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {coupons.map((coupon, index) => (
            <motion.div
              key={coupon._id || index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-green-300"
            >
              <div className="p-4 sm:p-5 lg:p-6 flex flex-col h-full">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2">
                    <span className="inline-flex px-3 py-1 text-xs sm:text-sm font-bold rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000] border border-green-200">
                      {getDiscountText(coupon)}
                    </span>
                    {coupon.validity?.endDate && (
                      <span className="text-[10px] sm:text-xs text-gray-500">
                        Expires: {formatDate(coupon.validity.endDate)}
                      </span>
                    )}
                  </div>

                  <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                    {coupon.name || coupon.code}
                  </h3>

                  {coupon.description && (
                    <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2">
                      {coupon.description}
                    </p>
                  )}

                  <div className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-5">
                    {coupon.minimumOrderAmount > 0 && (
                      <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-[#008000] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Min. order: ₹{coupon.minimumOrderAmount}</span>
                        {coupon.maximumDiscountAmount && (
                          <span className="ml-2">| Max: ₹{coupon.maximumDiscountAmount}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-auto pt-3 sm:pt-4 border-t border-gray-100">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm sm:text-base lg:text-lg font-bold text-[#008000]">
                        {coupon.code}
                      </span>
                    </div>
                    <button
                      onClick={() => handleCopyCode(coupon.code)}
                      className="w-full sm:w-auto bg-[#008000] hover:bg-[#008000] text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md text-center"
                    >
                      Use Code
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Copy Success Dialog */}
      <AnimatePresence>
        {showDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowDialog(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-[#008000]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-3">
                  Code Copied!
                </h3>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 text-center mb-2">Coupon Code:</p>
                  <p className="text-2xl font-bold text-[#008000] text-center font-mono">
                    {copiedCode}
                  </p>
                </div>

                <p className="text-sm sm:text-base text-gray-700 text-center mb-6">
                  The coupon code has been copied to your clipboard.
                  <span className="block mt-2 font-semibold text-gray-900">
                    Select the products you want to buy, then use this code at checkout to avail the discount!
                  </span>
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowDialog(false)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                  >
                    Got it
                  </button>
                  <button
                    onClick={() => {
                      setShowDialog(false);
                      window.location.href = '/';
                    }}
                    className="flex-1 bg-[#008000] hover:bg-[#008000] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                  >
                    Browse Products
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FeaturedOffers;

